import React, { useEffect, useState } from "react";
import api from "../../api";
import { Loader2, Plus, Trash2, Gift, CheckCircle2, Zap, RotateCcw, PoundSterling, Download, Banknote, CreditCard } from "lucide-react";
import { toast } from "../../hooks/use-toast";

export default function BulkTab() {
  const [events, setEvents] = useState(null);
  const [eventId, setEventId] = useState("");
  const [tierId, setTierId] = useState("");
  const [rows, setRows] = useState([{ name: "", email: "", phone: "" }]);
  const [paid, setPaid] = useState(false);
  const [payMethod, setPayMethod] = useState("cash");
  const [customPrice, setCustomPrice] = useState("");
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [lastSale, setLastSale] = useState(null);
  const [undoing, setUndoing] = useState(false);
  const [takings, setTakings] = useState(null);

  const loadTakings = () => api.get("/business/box-office/today").then(({ data }) => setTakings(data)).catch(() => {});

  useEffect(() => { api.get("/business/products").then(({ data }) => setEvents(data)); }, []);
  useEffect(() => { api.get("/business/me").then(({ data }) => setPresets(data.door_price_presets || [])).catch(() => {}); }, []);
  useEffect(() => { loadTakings(); }, []);

  const event = (events || []).find((e) => e.id === eventId);
  const tiers = event ? event.ticket_tiers || [] : [];
  const tier = tiers.find((t) => t.id === tierId);

  const addRow = () => setRows([...rows, { name: "", email: "", phone: "" }]);
  const rmRow = (i) => setRows(rows.filter((_, ri) => ri !== i));
  const setRow = (i, k, v) => setRows(rows.map((r, ri) => (ri === i ? { ...r, [k]: v } : r)));

  const issue = async (e) => {
    e.preventDefault();
    if (!eventId || !tierId) { toast({ title: "Select an event & tier", variant: "destructive" }); return; }
    const entries = rows.filter((r) => r.name || r.email || r.phone);
    if (entries.length === 0) { toast({ title: "Add at least one guest", variant: "destructive" }); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/business/comp-tickets", {
        product_id: eventId, tier_id: tierId, entries, paid,
        custom_price: paid && customPrice !== "" ? parseFloat(customPrice) : null,
        pay_method: payMethod,
        origin: window.location.origin,
      });
      setResult(data);
      setLastSale({ tickets: data.tickets, label: paid ? "sale" : "comps" });
      toast({ title: paid ? "Tickets issued" : "Comp tickets issued", description: `${data.count} ticket(s) created${data.emailed ? `, ${data.emailed} emailed` : ""}.` });
      setRows([{ name: "", email: "", phone: "" }]);
      loadTakings();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not issue comps", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const quickIssue = async (preset) => {
    if (!eventId || !tierId) { toast({ title: "Select an event & tier first", description: "Pick the event and ticket type below, then tap to sell.", variant: "destructive" }); return; }
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/business/comp-tickets", {
        product_id: eventId, tier_id: tierId, paid: true,
        entries: [{ name: "Walk-up guest", email: "", phone: "" }],
        custom_price: preset.price, pay_method: payMethod, origin: window.location.origin,
      });
      setResult(data);
      setLastSale({ tickets: data.tickets, label: `${preset.label} £${preset.price.toFixed(2)}` });
      toast({ title: `Sold — ${preset.label} £${preset.price.toFixed(2)} (${payMethod})`, description: `Ticket ${data.tickets?.[0]?.code || ""} issued.` });
      loadTakings();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not issue ticket", variant: "destructive" });
    } finally { setLoading(false); }
  };

  const undoLast = async () => {
    if (!lastSale?.tickets?.length) return;
    setUndoing(true);
    try {
      await Promise.all(lastSale.tickets.map((t) => api.post(`/business/tickets/${t.code}/cancel`)));
      toast({ title: "Sale voided", description: `${lastSale.tickets.length} ticket(s) cancelled and seat(s) released.` });
      setResult(null); setLastSale(null);
      loadTakings();
    } catch (err) {
      toast({ title: "Error", description: err?.response?.data?.detail || "Could not void the sale", variant: "destructive" });
    } finally { setUndoing(false); }
  };

  const downloadReport = async () => {
    try {
      const res = await api.get("/business/box-office/today.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "box-office-takings.csv"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast({ title: "Error", description: "Could not download takings report", variant: "destructive" });
    }
  };

  if (!events) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;

  return (
    <div className="max-w-[640px] space-y-5" data-testid="guestlist-tab">
      {takings && (
        <div className="bg-white rounded-2xl p-5 shadow-[0_8px_24px_rgba(20,30,55,0.06)]" data-testid="daily-takings">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#eefafa] flex items-center justify-center"><PoundSterling className="text-[#2ec9c9]" size={20} /></div>
              <div>
                <p className="text-[#8a97a8] text-sm">Today's box-office takings</p>
                <p className="text-xs text-[#8a97a8]">{takings.paid_count} paid · {takings.comp_count} comp</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <p className="text-2xl font-bold text-[#16243b]" data-testid="takings-total">£{takings.paid_total.toFixed(2)}</p>
              <button type="button" onClick={downloadReport} data-testid="download-takings" title="Download takings report"
                className="gp-btn-outline rounded-full px-3 py-2 text-sm font-medium flex items-center gap-1.5"><Download size={15} /> Report</button>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <div className="flex-1 rounded-xl bg-[#f6f8fb] px-4 py-2.5 flex items-center justify-between" data-testid="takings-cash">
              <span className="flex items-center gap-2 text-sm text-[#5a6b80]"><Banknote size={15} className="text-[#69c07a]" /> Cash <span className="text-xs text-[#8a97a8]">({takings.cash.count})</span></span>
              <span className="font-semibold text-[#16243b]">£{takings.cash.total.toFixed(2)}</span>
            </div>
            <div className="flex-1 rounded-xl bg-[#f6f8fb] px-4 py-2.5 flex items-center justify-between" data-testid="takings-card">
              <span className="flex items-center gap-2 text-sm text-[#5a6b80]"><CreditCard size={15} className="text-[#2ec9c9]" /> Card <span className="text-xs text-[#8a97a8]">({takings.card.count})</span></span>
              <span className="font-semibold text-[#16243b]">£{takings.card.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      )}
      <div className="bg-white rounded-2xl p-6 shadow-[0_8px_24px_rgba(20,30,55,0.06)]">
        <div className="flex items-center gap-2 mb-1"><Gift className="text-[#2ec9c9]" size={20} /><h3 className="font-semibold text-[#16243b]">Box office — issue tickets manually</h3></div>
        <p className="text-[#5a6b80] text-sm mb-4">Generate real QR tickets for guests who booked in person, VIPs or press. Tickets count against tier capacity; if you enter an email the ticket is emailed automatically.</p>
        <div className="flex gap-2 mb-5" data-testid="boxoffice-mode">
          <button type="button" onClick={() => setPaid(false)} data-testid="mode-comp" className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium border-2 transition-colors ${!paid ? "border-[#2ec9c9] bg-[#eefafa] text-[#16243b]" : "border-gray-200 text-[#5a6b80]"}`}>Complimentary (£0)</button>
          <button type="button" onClick={() => setPaid(true)} data-testid="mode-paid" className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium border-2 transition-colors ${paid ? "border-[#2ec9c9] bg-[#eefafa] text-[#16243b]" : "border-gray-200 text-[#5a6b80]"}`}>Paid in-house{tier ? ` (£${tier.price.toFixed(2)})` : ""}</button>
        </div>
        {paid && <p className="text-xs text-[#8a97a8] -mt-3 mb-4">In-house sales appear in your revenue reports but aren't part of platform payouts and carry no platform fee (you took the money directly).</p>}
        {paid && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Payment method</label>
            <div className="flex gap-2" data-testid="pay-method">
              <button type="button" onClick={() => setPayMethod("cash")} data-testid="method-cash"
                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium border-2 transition-colors flex items-center justify-center gap-2 ${payMethod === "cash" ? "border-[#2ec9c9] bg-[#eefafa] text-[#16243b]" : "border-gray-200 text-[#5a6b80]"}`}><Banknote size={16} /> Cash</button>
              <button type="button" onClick={() => setPayMethod("card")} data-testid="method-card"
                className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium border-2 transition-colors flex items-center justify-center gap-2 ${payMethod === "card" ? "border-[#2ec9c9] bg-[#eefafa] text-[#16243b]" : "border-gray-200 text-[#5a6b80]"}`}><CreditCard size={16} /> Card</button>
            </div>
          </div>
        )}
        {paid && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-[#16243b] mb-1.5">Door price override (optional)</label>
            {presets.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="door-presets">
                {presets.map((p) => {
                  const active = customPrice !== "" && parseFloat(customPrice) === p.price;
                  return (
                    <button key={p.id} type="button" onClick={() => setCustomPrice(String(p.price))} data-testid={`door-preset-${p.id}`}
                      className={`rounded-full px-3.5 py-1.5 text-sm font-medium border-2 transition-colors ${active ? "border-[#2ec9c9] bg-[#eefafa] text-[#16243b]" : "border-gray-200 text-[#5a6b80] hover:border-[#2ec9c9]"}`}>
                      {p.label} · £{p.price.toFixed(2)}
                    </button>
                  );
                })}
                {customPrice !== "" && (
                  <button type="button" onClick={() => setCustomPrice("")} data-testid="door-preset-clear" className="rounded-full px-3.5 py-1.5 text-sm font-medium text-red-400 hover:text-red-600">Clear</button>
                )}
              </div>
            )}
            <input type="number" step="0.01" min="0" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)} data-testid="comp-custom-price"
              placeholder={tier ? `Tier price £${tier.price.toFixed(2)}` : "Leave blank for tier price"}
              className="w-full sm:w-56 rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9]" />
            <p className="text-xs text-[#8a97a8] mt-1">Charge a one-off door price instead of the tier price. {presets.length === 0 ? "Save presets in Settings for one-tap prices. " : ""}Leave blank to use £{tier ? tier.price.toFixed(2) : "0.00"}.</p>
          </div>
        )}
        {paid && presets.length > 0 && (
          <div className="mb-4 rounded-xl bg-[#f6f8fb] p-4" data-testid="quick-sell">
            <div className="flex items-center gap-2 mb-1"><Zap className="text-[#f4b740]" size={16} /><span className="text-sm font-semibold text-[#16243b]">Quick sell (walk-up)</span></div>
            <p className="text-xs text-[#8a97a8] mb-3">Tap a price to instantly issue one ticket for a walk-up guest — no form needed. {(!eventId || !tierId) && "Select an event & ticket type below first."}</p>
            <div className="flex flex-wrap gap-2">
              {presets.map((p) => (
                <button key={p.id} type="button" disabled={loading || !eventId || !tierId} onClick={() => quickIssue(p)} data-testid={`quick-sell-${p.id}`}
                  className="rounded-full px-4 py-2 text-sm font-medium bg-[#16243b] text-white hover:bg-[#20344f] transition-colors disabled:opacity-40 flex items-center gap-1.5">
                  <Zap size={13} /> {p.label} · £{p.price.toFixed(2)}
                </button>
              ))}
            </div>
          </div>
        )}
        <form onSubmit={issue} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">Event</label>
              <select value={eventId} onChange={(e) => { setEventId(e.target.value); setTierId(""); }} data-testid="comp-event-select"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white">
                <option value="">Select an event…</option>
                {events.map((e) => <option key={e.id} value={e.id}>{e.title}{e.event_date ? ` — ${new Date(e.event_date).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}` : ""}{e.venue_name ? ` · ${e.venue_name}` : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#16243b] mb-1.5">Ticket type</label>
              <select value={tierId} onChange={(e) => setTierId(e.target.value)} disabled={!event} data-testid="comp-tier-select"
                className="w-full rounded-xl border border-gray-200 px-4 py-2.5 outline-none focus:border-[#2ec9c9] bg-white disabled:opacity-50">
                <option value="">Select a tier…</option>
                {tiers.map((t) => <option key={t.id} value={t.id}>{t.name} {t.capacity > 0 ? `(${t.capacity - (t.sold || 0)} left)` : ""}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#16243b] mb-2">Guests</label>
            <div className="space-y-2">
              {rows.map((r, i) => (
                <div key={i} className="flex gap-2" data-testid={`comp-row-${i}`}>
                  <input placeholder="Guest name" value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} data-testid={`comp-name-${i}`}
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                  <input type="email" placeholder="Email (optional)" value={r.email} onChange={(e) => setRow(i, "email", e.target.value)} data-testid={`comp-email-${i}`}
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                  <input type="tel" placeholder="Mobile (optional)" value={r.phone} onChange={(e) => setRow(i, "phone", e.target.value)} data-testid={`comp-phone-${i}`}
                    className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#2ec9c9]" />
                  <button type="button" onClick={() => rmRow(i)} className="text-red-400 hover:text-red-600 p-1"><Trash2 size={15} /></button>
                </div>
              ))}
              <button type="button" onClick={addRow} data-testid="comp-add-row" className="text-[#1fb0b0] text-sm font-medium flex items-center gap-1"><Plus size={14} /> Add guest</button>
            </div>
          </div>

          <button disabled={loading} data-testid="comp-issue-btn" className="gp-btn-teal rounded-xl px-6 py-3 font-medium flex items-center gap-2 disabled:opacity-60">
            {loading ? <Loader2 className="animate-spin" size={18} /> : <Gift size={18} />} {paid ? "Issue paid tickets" : "Issue comp tickets"}
          </button>
        </form>

        {result && (
          <div className="mt-6 bg-green-50 border border-green-100 rounded-xl p-4" data-testid="comp-result">
            <div className="flex items-center justify-between">
              <p className="text-green-700 font-semibold flex items-center gap-2"><CheckCircle2 size={18} /> {result.count} ticket(s) issued</p>
              {lastSale?.tickets?.length > 0 && (
                <button type="button" onClick={undoLast} disabled={undoing} data-testid="undo-last-sale" className="flex items-center gap-1.5 text-sm font-medium text-red-500 hover:text-red-700 disabled:opacity-50">
                  {undoing ? <Loader2 className="animate-spin" size={14} /> : <RotateCcw size={14} />} Undo last sale
                </button>
              )}
            </div>
            <div className="mt-2 space-y-1">
              {result.tickets.map((t) => (
                <div key={t.id} className="flex items-center justify-between text-sm">
                  <span className="text-[#5a6b80]">{t.attendee_name}</span>
                  <a href={`/voucher/${t.code}`} target="_blank" rel="noreferrer" className="font-mono text-[#16243b] hover:text-[#1fb0b0]">{t.code}</a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
